<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

  import type { NightsRange } from '../model/types';
  import { useNightsRange } from '../model/useNightsRange';
  import NightsRangeDropdown from './NightsRangeDropdown.vue';
  import { NIGHTS_RANGE_TEXTS } from '../config/texts';

  interface NightsRangeFieldProps {
    modelValue?: NightsRange | null;
    checkInLabel?: string;
  }

  const props = withDefaults(defineProps<NightsRangeFieldProps>(), {
    modelValue: null,
    checkInLabel: '',
  });

  interface NightsRangeFieldEmits {
    (e: 'update:modelValue', range: NightsRange): void;
    (e: 'change', range: NightsRange): void;
    (e: 'manual'): void;
  }

  const emit = defineEmits<NightsRangeFieldEmits>();

  const core = useNightsRange({
    initial: props.modelValue,
    checkInLabel: () => props.checkInLabel,
  });

  const rootRef = ref<HTMLElement | null>(null);

  const onToggle = (): void => {
    core.toggle();
  };

  const onOutsidePointer = (event: MouseEvent): void => {
    const root = rootRef.value;
    const target = event.target as Node | null;
    if (root && target && !root.contains(target)) {
      core.close();
    }
  };

  const onUpdateFrom = (data: number) => {
    core.setFromValue(data);
    emit('manual');
  };

  const onUpdateTo = (data: number) => {
    core.setToValue(data);
    emit('manual');
  };

  watch(core.controlActivated, (active) => {
    if (active) {
      document.addEventListener('mousedown', onOutsidePointer);
    } else {
      document.removeEventListener('mousedown', onOutsidePointer);
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onOutsidePointer);
  });

  watch(core.selection, (range) => {
    emit('update:modelValue', range);
    emit('change', range);
  });

  watch(
    () => props.modelValue,
    (next) => {
      if (!next) {
        return;
      }
      if (next.from !== core.from.value || next.to !== core.to.value) {
        core.restore(next);
      }
    },
  );

  onMounted(() => {
    core.restore(props.modelValue);
  });

  defineExpose({
    validate: (): boolean => core.validate(),
  });
</script>

<template>
  <div ref="rootRef" class="nights-range-field" data-testid="nights-range-field">
    <NightsRangeDropdown
      :from="core.from.value"
      :to="core.to.value"
      :visible="core.controlActivated.value"
      :summary="core.summaryLabel.value"
      @update:from="onUpdateFrom"
      @update:to="onUpdateTo"
      @close="core.close"
    >
      <template #reference>
        <div
          class="nights-range-field__control"
          :class="{ 'nights-range-field__control_active': core.controlActivated.value }"
          @click="onToggle"
        >
          <Typo
            class="nights-range-field__label"
            :variant="TypoVariant.BodySRegular"
            tag="span"
          >{{ NIGHTS_RANGE_TEXTS.fieldLabel }}</Typo>

          <div class="nights-range-field__value" data-testid="nights-range-value">
            <Typo :variant="TypoVariant.BodyLRegular" tag="span">{{ core.valueLabel.value }}</Typo>
          </div>
        </div>
      </template>
    </NightsRangeDropdown>
  </div>
</template>

<style scoped lang="scss">
  .nights-range-field {
    position: relative;
    width: 100%;

    &__control {
      background: var(--color-bg-primary);
      box-sizing: border-box;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxs);
      height: 54px;
      justify-content: center;
      padding: var(--spacing-xs) var(--spacing-s);
      transition: background-color var(--transition-default);

      &_active {
        background: var(--color-disabled-100);
      }
    }

    &__label {
      color: var(--color-text-secondary);
      pointer-events: none;
      user-select: none;
    }

    &__value {
      color: var(--color-text-primary);
    }
  }
</style>
